import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Shield, UserCheck, Zap, Clock, Users, Home, Leaf, Star, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { COMPANY_INFO } from '../lib/utils';
import { SEOHead } from '../components/SEOHead';
import { VIEWPORT_ONCE } from '../lib/motion';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const TRUST_ITEMS = [
  { icon: Shield, title: 'Fiabilité', desc: 'Nos Easers sont soigneusement sélectionnés, formés et suivis pour garantir un service de qualité supérieure.' },
  { icon: UserCheck, title: 'Easer dédié', desc: 'Un interlocuteur unique qui connaît vos habitudes et votre domicile. Continuité et confiance garanties.' },
  { icon: Zap, title: 'Avance immédiate', desc: "Vous ne payez que le reste à charge grâce à l'avance immédiate du crédit d'impôt (Urssaf)." },
  { icon: Heart, title: 'Satisfaction garantie', desc: 'Nous garantissons la qualité de nos services avec des solutions qui répondent à vos besoins.' },
];

export default function NotreVisionPage() {
  return (
    <div>
      <SEOHead
        title="Notre vision — Facilitateur de vies"
        description="Ptitservices.com, facilitateur de vies. Pour les actifs : libérez du temps pour profiter de vos enfants. Pour les retraités : restez chez vous en toute autonomie. Easer dédié, bienveillance et professionnalisme."
        path="/notre-vision"
      />
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Notre vision</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Notre vision</Badge>
              <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
                Bien plus qu'un prestataire :<br />
                <span className="text-[hsl(var(--primary))]">facilitateur de vies</span>
              </h1>
              <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
                Chez Ptitservices.com, notre métier va au-delà du ménage ou du jardinage. 
                Nous sommes des <strong>facilitateurs de vies</strong>. Notre mission : vous libérer des tâches du quotidien 
                pour que vous puissiez consacrer votre temps à ce qui compte vraiment.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1758687126877-b37052a20a4d?w=600&h=400&fit=crop" 
                  alt="Famille heureuse profitant de la vie ensemble" 
                  className="w-full h-[300px] sm:h-[360px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pour les actifs */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1758687125866-6b9d86d41cc5?w=600&h=400&fit=crop" 
                  alt="Père et fils profitant d'un moment ensemble" 
                  className="w-full h-[280px] sm:h-[340px] object-cover"
                />
              </div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Pour les actifs</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Libérez du temps pour profiter de la vie</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Entre le travail, les enfants, les activités et les obligations du quotidien, le temps est précieux. 
                Chaque heure passée à faire le ménage ou à entretenir le jardin est une heure de moins avec vos proches.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                En confiant l'entretien de votre domicile à votre Easer dédié, vous retrouvez des heures précieuses chaque semaine. 
                Du temps pour <strong>profiter de vos enfants</strong>, pour vous détendre, pour vivre pleinement. 
                Votre maison est impeccable, votre jardin est soigné, et vous n'avez rien eu à faire.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Plus de temps en famille, pour vos loisirs et vos passions</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Un intérieur toujours propre sans effort, un jardin toujours entretenu</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Moins de 15 €/h après avance immédiate pour le ménage</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pour les retraités */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp} className="order-2 lg:order-1">
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Pour les retraités</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Rester chez soi le plus longtemps possible</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Votre maison, c'est votre histoire, vos souvenirs, votre confort. Nous croyons que chacun mérite de 
                <strong> rester chez soi en toute autonomie</strong>, le plus longtemps possible.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Nos Easers vous accompagnent avec bienveillance dans l'entretien de votre domicile et de votre jardin. 
                Ils vous aident également pour les courses, la préparation des repas ou les démarches administratives. 
                Un soutien discret et respectueux qui vous permet de <strong>conserver votre indépendance</strong> et votre qualité de vie.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Maintien à domicile dans le confort et la dignité</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Un Easer attitré, bienveillant, qui vous connaît</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Accompagnement aux courses, aide aux repas, assistance administrative</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Crédit d'impôt 50% pour tous, y compris les retraités</span>
                </div>
              </div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp} className="order-1 lg:order-2">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1658632302262-957f74b1fdd9?w=600&h=400&fit=crop" 
                  alt="Personne âgée heureuse chez elle" 
                  className="w-full h-[280px] sm:h-[340px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous - Trust items */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Pourquoi nous ?</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Les garanties Ptitservices</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Un seul prestataire pour une gamme complète de solutions couvrant l'ensemble des besoins de votre domicile et de votre jardin.</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {TRUST_ITEMS.map((item, i) => (
              <motion.div key={`item-${i}`} variants={fadeUp}>
                <Card className="p-6 h-full bg-card border border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Notre engagement */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Notre engagement</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold mb-2">Bienveillance</h3>
                <p className="text-sm text-muted-foreground">Chaque intervention est réalisée avec respect, écoute et attention. Votre bien-être est notre priorité.</p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold mb-2">Proximité</h3>
                <p className="text-sm text-muted-foreground">Ancrés dans l'Artois, nos Easers sont vos voisins. Ils s'engagent dans la durée à vos côtés.</p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-4">
                  <Star className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold mb-2">Excellence</h3>
                <p className="text-sm text-muted-foreground">Sélection rigoureuse, formation continue et suivi qualité permanent pour un service irréprochable.</p>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Facilitez-vous la vie dès aujourd'hui</h2>
            <p className="text-primary-foreground/80 mb-8">Que vous soyez actif ou retraité, nous adaptons nos services à vos besoins. Devis gratuit et sans engagement.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/devis">
                <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href={`tel:${COMPANY_INFO.phoneRaw}`}>
                <Button size="lg" variant="secondary">
                  <Phone className="mr-2 h-4 w-4" />
                  {COMPANY_INFO.phone}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
